/**
 * `HttpCtx` + `useHttp()` — a thin context exposing the shared
 * `HttpClient` to views that talk to endpoints which haven't been
 * promoted to the `SillApi` bag in `@sill/types/api`.
 *
 * The buyer-detail surface (ADR-0065 §6–§8) is the first such consumer:
 * its four endpoints are dashboard-scoped (single-record decrypt,
 * purge, retention GET/PUT) and don't fit cleanly into any of the
 * existing typed bags. Rather than synthesize a `BuyerDetailAPI`
 * interface in `@sill/types`, we let the per-feature client modules
 * (`api/audit-buyer-detail.ts`, `api/account-buyer-detail-retention.ts`)
 * consume `HttpClient` directly and surface a discriminated-union
 * result.
 *
 * Production wires the singleton from `app/providers.tsx`; tests wire a
 * stub via `<HttpCtx.Provider value={stub} />`. Same pattern as
 * `ApiCtx` / `ApiProvider`.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { HttpClient } from './http-client.js';

export const HttpCtx = createContext<HttpClient | null>(null);

export interface HttpProviderProps {
  http: HttpClient;
  children: ReactNode;
}

export function HttpProvider({ http, children }: HttpProviderProps): JSX.Element {
  return <HttpCtx.Provider value={http}>{children}</HttpCtx.Provider>;
}

export function useHttp(): HttpClient {
  const ctx = useContext(HttpCtx);
  if (!ctx) {
    throw new Error('useHttp() called outside <HttpProvider>');
  }
  return ctx;
}

/**
 * Same as `useHttp()` but returns `null` when no provider is mounted
 * rather than throwing. Used by features that should gracefully no-op
 * in test scaffolds that haven't been migrated to wire the
 * `<HttpProvider />` yet — those scaffolds keep working, the feature
 * just renders its empty/loading state. Production always returns a
 * non-null client because `app/providers.tsx` mounts the provider
 * unconditionally.
 */
export function useOptionalHttp(): HttpClient | null {
  return useContext(HttpCtx);
}
